import { UseTableFeaturesOptions } from '@fluentui/react-table';
import {
  Button,
  makeStyles,
  SearchBox,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableRowData,
  TableRowId,
  TableSelectionCell,
  Text,
  Tooltip,
  useFocusableGroup,
  useTableFeatures,
  useTableSelection,
} from '@fluentui/react-components';
import React, {
  FunctionComponent,
  ComponentProps,
  MutableRefObject,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import getIn from '../connectedForm/utils/getIn';
import Icon from '@mdi/react';
import { mdiCloseCircleOutline } from '@mdi/js';
import theme from '../../styles/themes/default/defaultTheme';
import titleCase from '../../utils/TitleCase';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { IFilterInput, Maybe } from '../../graphql/schema/p4types';
import { basicRemoteFilterGetter } from '../FluentWrapper/FluentList';
import { IRemoteFilterGetter } from '../../@types/entityTypes';
import rowActionsCell from '../react-data-grid-addons/src/draggable/RowActionsCell';
import { BluePill } from '../DropdownEditor/CustomOptions';
import { SearchBoxProps } from '@fluentui/react-search';

interface ReactWindowRenderFnProps<TItem> extends ListChildComponentProps {
  data: ExtendedTableRowData<TItem>[];
}

interface ExtendedTableRowData<TItem> extends TableRowData<TItem> {
  onClick: (e: React.MouseEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  selected: boolean;
  appearance: 'brand' | 'none';
}

const useFluentTableStyles = makeStyles({
  searchBox: {
    maxWidth: '100%',
    width: '100%',
  },
});

export enum IFluentTableRowSize {
  ExtraSmall = 'extra-small',
  Small = 'small',
  Medium = 'medium',
}

export type IRowActionCells = (options: { item: any; rowIndex: number }) => ReactElement;

const FluentViewTableRow = <TItem,>({
  selected,
  appearance,
  onClick,
  onKeyDown,
  index,
  item,
  style,
  selectionKey,
  selectionMode,
  tableFeatureOptions,
  actionCells: ActionCells,
}: Omit<ExtendedTableRowData<TItem>, 'rowId'> & {
  tableFeatureOptions: UseTableFeaturesOptions<TItem>;
  index: number;
  style: React.CSSProperties;
  selectionKey: string;
  selectionMode: 'single' | 'multiselect' | 'none';
  actionCells?: IRowActionCells;
}) => {
  const key = (item as any)?.[selectionKey];

  const actionCells = ActionCells ? <ActionCells item={item} rowIndex={index} /> : null;

  return (
    <TableRow
      aria-rowindex={index + 2}
      style={style}
      key={key ?? 'unknown'}
      onKeyDown={onKeyDown}
      onClick={onClick}
      appearance={appearance}
    >
      {selectionMode === 'none' ? null : (
        <TableSelectionCell
          checked={selected}
          checkboxIndicator={{ 'aria-label': 'Select row' }}
          type={selectionMode === 'single' ? 'radio' : 'checkbox'}
        />
      )}
      {tableFeatureOptions.columns.map((column) => (
        <TableCell key={`${key}-${column.columnId}`}>
          {item ? column.renderCell?.(item) ?? getIn(item, column.columnId) : null}
        </TableCell>
      ))}
      {actionCells}
    </TableRow>
  );
};

const FluentTableComponent = ({
  height,
  itemSize,
  itemCount,
  itemData,
  RenderRow,
  infiniteScrollConfig,
  infiniteLoaderRef,
}: {
  itemCount: number;
  height: number;
  itemSize: number;
  itemData: any;
  RenderRow: ({ index, style, data }: ReactWindowRenderFnProps<any>) => null | ReactElement;
  infiniteScrollConfig?: IInfiniteScrollConfig;
  infiniteLoaderRef?: MutableRefObject<any>;
}) => {
  const localInfiniteLoadRef = useRef<any>(null);
  const infiniteLoaderRefFunction = useCallback(
    (ref) => {
      localInfiniteLoadRef.current = ref;
      if (infiniteLoaderRef) {
        infiniteLoaderRef.current = ref;
      }
    },
    [infiniteLoaderRef]
  );

  if (infiniteScrollConfig) {
    return (
      <AutoSizer disableHeight>
        {({ width }: { width: number }) => (
          <InfiniteLoader
            ref={infiniteLoaderRefFunction}
            isItemLoaded={infiniteScrollConfig.isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={infiniteScrollConfig.loadMoreItems}
          >
            {({ onItemsRendered, ref }) => (
              <FixedSizeList
                height={height}
                width={width}
                itemSize={itemSize}
                itemCount={itemCount}
                itemData={itemData as any}
                onItemsRendered={onItemsRendered}
                ref={ref}
              >
                {RenderRow}
              </FixedSizeList>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    );
  }
  return (
    <AutoSizer disableHeight>
      {({ width }: { width: number }) => (
        <FixedSizeList
          height={height}
          width={width}
          itemSize={itemSize}
          itemCount={itemCount}
          itemData={itemData as any}
        >
          {RenderRow}
        </FixedSizeList>
      )}
    </AutoSizer>
  );
};

type IInfiniteScrollConfig = {
  isItemLoaded: (index: number) => boolean;
  loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void> | void;
  setRemoteFilters: (newFilters: Array<IFilterInput>) => void;
  remoteFilterGetter?: IRemoteFilterGetter;
};

/**
 * Wrapper for fluent-ui 'Table' component primarily intended for use in the EditableDataGrid View Manager.
 * Should be safe for re-use, just noting the original intent.
 */
export const FluentTable = <TItem,>({
  itemCount,
  height,
  tableFeatureOptions,
  onSelectionChange,
  selectionMode,
  selectionKey,
  selectedItems,
  size = IFluentTableRowSize.Medium,
  onLocalSearchChange,
  infiniteScrollConfig,
  infiniteLoaderRef,
  rowActionCells,
  disableHeader,
}: {
  itemCount: number;
  height: number; //Height of the ROWS part only (Header is ~66px tall)
  tableFeatureOptions: UseTableFeaturesOptions<TItem>;
  onSelectionChange?: (e: SyntheticEvent, data: { selectedItems: Set<TableRowId> }) => void;
  selectionMode: 'single' | 'multiselect' | 'none';
  selectionKey: string;
  selectedItems?: Set<TableRowId>; //If undefined, FluentTable will manage its own selection list.
  size?: IFluentTableRowSize;
  onLocalSearchChange?: SearchBoxProps['onChange']; //Optional provision for local filtering of data. Do not provide if using infinite scroll.
  infiniteScrollConfig?: IInfiniteScrollConfig;
  infiniteLoaderRef?: MutableRefObject<{ resetloadMoreItemsCache: (autoReload?: boolean) => void }>;
  rowActionCells?: IRowActionCells;
  disableHeader?: boolean;
}) => {
  const [previousSelectedOptionsKeys, setPreviousSelectedOptionsKeys] = useState<Set<TableRowId>>(new Set());
  const styles = useFluentTableStyles();

  const lastSelected = useRef<TableRowId | null>(null);

  useEffect(() => {
    setPreviousSelectedOptionsKeys(new Set(Array.from(selectedItems ?? [])?.map((object) => object) ?? []));
  }, [selectedItems]);

  const onShiftSelectionChange = useCallback(
    (e: SyntheticEvent, data: { selectedItems: Set<TableRowId> }) => {
      //In lieu of https://github.com/tc39/proposal-set-methods being merged, this is the method I'm aware of to determine the newly selected item.
      //As far as I'm aware, there's no guarantee that the 'last' item in the set is the newest, and the fluent-ui Table doesn't give us this information.
      const newSelections = [...data.selectedItems].filter((x) => !previousSelectedOptionsKeys.has(x));
      const options = tableFeatureOptions.items;

      const newSelection = newSelections.length === 1 ? newSelections[0] : null;
      if (newSelection && lastSelected.current && (e as unknown as MouseEvent).shiftKey) {
        let startTagging = false;
        //Loop through options - When we hit one of our shift-selected items, start tagging other items as selected until we reach the target item, then break.
        for (let i = 0; i < options.length; i++) {
          const option: any = options[i];
          if (!option) continue; //Skip null entries (If user skips-pages with a fast scroll - Nothing for us to tag).
          const optionId: string = option[selectionKey]!;
          if (optionId === lastSelected.current || optionId === newSelection) {
            if (startTagging) {
              break;
            } else {
              startTagging = true;
            }
          } else if (startTagging) {
            data.selectedItems.add(optionId!);
          }
        }
      }
      lastSelected.current = newSelection;

      onSelectionChange?.(e, data);
      //We need to clone the set for this because the fluent-ui table simple pushes items into the same set (And we need to compare to determine the added item).
      setPreviousSelectedOptionsKeys(new Set(data.selectedItems));
    },
    [onSelectionChange, previousSelectedOptionsKeys, selectionKey, tableFeatureOptions.items]
  );

  const {
    selection: { toggleRow, isRowSelected },
  } = useTableFeatures<TItem>(tableFeatureOptions, [
    useTableSelection({
      selectionMode: selectionMode === 'none' ? 'single' : selectionMode, //'none' Acts like single, but there's no checkbox/radio column.
      selectedItems: selectedItems,
      onSelectionChange: onShiftSelectionChange,
    }),
  ]);

  const itemData: Array<ExtendedTableRowData<TItem>> = useMemo(
    () =>
      tableFeatureOptions.items.map((item) => {
        const key = (item as Maybe<any>)?.[selectionKey] as string;
        const selected = isRowSelected(key);
        const rowData: Omit<ExtendedTableRowData<TItem>, 'rowId'> = {
          item: item,
          onClick: (e: React.MouseEvent) => toggleRow(e, key),
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === ' ') {
              e.preventDefault();
              toggleRow(e, key);
            }
          },
          selected,
          appearance: selected ? ('brand' as const) : ('none' as const),
        };
        return rowData as ExtendedTableRowData<TItem>;
      }),
    [isRowSelected, selectionKey, tableFeatureOptions.items, toggleRow]
  );

  const RenderRow = useCallback(
    ({ index, style, data }: ReactWindowRenderFnProps<TItem>) => {
      if (!data[index]) return null;
      const { item, selected, appearance, onClick, onKeyDown } = data[index];

      return (
        <FluentViewTableRow
          selected={selected}
          appearance={appearance}
          onClick={onClick}
          onKeyDown={onKeyDown}
          item={item}
          index={index}
          style={style}
          selectionMode={selectionMode}
          selectionKey={selectionKey}
          tableFeatureOptions={tableFeatureOptions}
          actionCells={rowActionCells}
        />
      );
    },
    [rowActionCells, selectionKey, selectionMode, tableFeatureOptions]
  );

  //Alternate table sizing, if we feel the current appearance is overly spacious.
  let itemSize: number;
  switch (size) {
    case IFluentTableRowSize.ExtraSmall:
      itemSize = 30; //Used by FilterDetailsList
      break;
    case IFluentTableRowSize.Small:
      itemSize = 35;
      break;
    case IFluentTableRowSize.Medium:
      itemSize = 45; //Used by EditableDataGridViewController
      break;
    default:
      throw new Error('Invalid item size provided');
  }

  //For the SearchBox elements, ideally, 'contentAfter' would be always displayed (For some reason it's hidden by default if the box isn't focused), but
  // it's convenient vs faffing with overlay styles/etc.
  let search: ReactNode = null;
  if (infiniteScrollConfig) {
    search = (
      <SearchBox
        onChange={(ev, data) => {
          const newValue = data.value;
          const getter = infiniteScrollConfig.remoteFilterGetter ?? basicRemoteFilterGetter;
          infiniteScrollConfig.setRemoteFilters(
            getter({
              fieldName: tableFeatureOptions.columns[0].columnId as string,
              fieldValue: newValue ?? '',
            })
          );
        }}
        appearance={'underline'}
        placeholder={'Search'}
        contentAfter={
          selectionMode === 'multiselect' ? <div>{`${selectedItems?.size ?? 0} Items Selected`}</div> : undefined
        }
        className={styles.searchBox}
      />
    );
  } else if (onLocalSearchChange) {
    search = (
      <SearchBox
        onChange={onLocalSearchChange}
        appearance={'underline'}
        placeholder={'Search'}
        contentAfter={
          selectionMode === 'multiselect' ? <div>{`${selectedItems?.size ?? 0} Items Selected`}</div> : undefined
        }
        className={styles.searchBox}
      />
    );
  }

  return (
    <>
      {search}
      <Table aria-label={'View selection table'} noNativeElements size={size}>
        {disableHeader ? null : (
          <TableHeader>
            <TableRow>
              <div style={{ maxWidth: '44px', minWidth: '44px', textAlign: 'center' }}>
                {selectionMode === 'multiselect' ? (
                  <Button
                    icon={<Icon path={mdiCloseCircleOutline} size={1} color={theme.colors.grey120} />}
                    aria-label="Clear"
                    onClick={(ev) => onShiftSelectionChange(ev, { selectedItems: new Set<TableRowId>() })}
                    appearance={'subtle'}
                  />
                ) : null}
              </div>
              {tableFeatureOptions.columns.map((column) => (
                <TableHeaderCell key={column.columnId}>{titleCase(column.columnId)}</TableHeaderCell>
              ))}
              {rowActionCells ? <TableHeaderCell /> : null}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          <FluentTableComponent
            itemCount={itemCount}
            itemData={itemData}
            itemSize={itemSize}
            height={height}
            RenderRow={RenderRow}
            infiniteScrollConfig={infiniteScrollConfig}
            infiniteLoaderRef={infiniteLoaderRef}
          />
        </TableBody>
      </Table>
    </>
  );
};
